
import { supabase } from "@/integrations/supabase/client";

export interface YouTubeVideo {
  id: string;
  technique_id: string;
  user_id: string;
  youtube_url: string;
  title: string | null;
  notes: string | null;
  created_at: string;
  video_id?: string; // Extracted video ID for embedding
}

export interface AddVideoData {
  technique_id: string;
  url: string;
  title?: string;
  notes?: string;
}

// Get all videos for a technique
export async function getVideosForTechnique(technique_id: string): Promise<YouTubeVideo[]> {
  try {
    const { data, error } = await supabase
      .from('youtube_videos')
      .select('*')
      .eq('technique_id', technique_id)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    // Return data directly with video_id extraction
    return data.map((item: Record<string, unknown>) => ({
      ...item,
      video_id: extractVideoId(item.youtube_url as string)
    }));
  } catch (error) {
    console.error('Error fetching videos for technique:', error);
    throw error;
  }
}

// Add a new video to a technique
export async function addVideo(data: AddVideoData): Promise<YouTubeVideo> {
  try {
    // Get the current authenticated user
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User must be authenticated to add videos');
    }

    const { error } = await supabase
      .from('youtube_videos')
      .insert({
        technique_id: data.technique_id,
        youtube_url: data.url,
        title: data.title || null,
        notes: data.notes || null,
        user_id: user.id
      });

    if (error) throw error;

    // Fetch the newly created video to return it
    const { data: videos, error: fetchError } = await supabase
      .from('youtube_videos')
      .select('*')
      .eq('technique_id', data.technique_id)
      .eq('user_id', user.id)
      .eq('youtube_url', data.url)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (fetchError) throw fetchError;

    // Return the result with video_id
    return {
      ...videos,
      video_id: extractVideoId(videos.youtube_url)
    };
  } catch (error) {
    console.error('Error adding video to technique:', error);
    throw error;
  }
}

// Add a new video to a technique (legacy compatibility)
export async function addVideoToTechnique(
  technique_id: string, 
  youtube_url: string, 
  title?: string, 
  notes?: string
): Promise<YouTubeVideo> {
  return addVideo({
    technique_id,
    url: youtube_url,
    title,
    notes
  });
}

// Delete a video
export async function deleteVideo(videoId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('youtube_videos')
      .delete()
      .eq('id', videoId);
    
    if (error) throw error;
  } catch (error) {
    console.error('Error deleting video:', error);
    throw error;
  }
}

// Helper function to extract video ID from YouTube URL
export function extractVideoId(url: string): string | undefined {
  if (!url) return undefined;
  
  // Handle youtube.com/watch?v= format
  let match = url.match(/(?:youtube\.com\/watch\?v=)([^&\s]+)/);
  if (match) return match[1];
  
  // Handle youtu.be/ format
  match = url.match(/(?:youtu\.be\/)([^&\s?]+)/);
  if (match) return match[1];
  
  // Handle youtube.com/embed/ format
  match = url.match(/(?:youtube\.com\/embed\/)([^&\s?]+)/);
  if (match) return match[1];
  
  return undefined;
}

// Validate a YouTube URL
export function isValidYouTubeUrl(url: string): boolean {
  return !!extractVideoId(url);
}
